/**
 * Two components, one file, because they are two states of one decision:
 *
 *   InfoNotice    — the default. Non-blocking, dismissible, never covers the
 *                   map, never traps focus. Rendered when everything the site
 *                   stores is consent-exempt, which is the case today.
 *   ConsentPanel  — the GDPR + § 89 ZEK consent layer. Built, tested, and
 *                   switched off behind VITE_CONSENT_REQUIRED. Turn it on in
 *                   the same commit that adds anything non-essential.
 *
 * Neither may break the map: both are rendered inside an error boundary in
 * App.tsx, and neither is on the path that mounts MapLibre.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONSENT_REQUIRED,
  dismissNotice,
  needsConsentPrompt,
  noticeDismissed,
  readConsent,
  writeConsent,
} from '@/lib/consent';

/* ------------------------------------------------------------------ */
/* Default: a slim information notice                                  */
/* ------------------------------------------------------------------ */

export function InfoNotice() {
  const [visible, setVisible] = useState(() => !noticeDismissed());

  if (CONSENT_REQUIRED || !visible) return null;

  return (
    // `sticky` at the bottom of the document flow, not fixed over the map:
    // the requirement is that it never covers the map or blocks interaction.
    <aside
      role="note"
      aria-label="Informace o ukládání údajů"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700"
    >
      {/* One sentence, deliberately. A notice long enough to need scrolling on
          a phone is a banner, and a banner is what this is not. */}
      <p className="max-w-3xl">
        Neukládáme žádné analytické ani marketingové cookies — jen to, co je nutné pro funkci webu.{' '}
        <Link className="underline hover:no-underline" to="/soukromi">
          Ochrana soukromí
        </Link>
      </p>
      <button
        type="button"
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 font-medium hover:bg-slate-100"
        onClick={() => {
          dismissNotice();
          setVisible(false);
        }}
      >
        Rozumím
      </button>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Feature-flagged: the real consent layer                             */
/* ------------------------------------------------------------------ */

interface Choice {
  analytics: boolean;
  marketing: boolean;
}

export function ConsentPanel() {
  const [open, setOpen] = useState(() => needsConsentPrompt());
  // No pre-ticked boxes: both optional categories start off, always.
  const [choice, setChoice] = useState<Choice>({ analytics: false, marketing: false });
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusable = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const acceptAll = useCallback(() => {
    writeConsent({ analytics: true, marketing: true });
    close();
  }, [close]);

  const rejectAll = useCallback(() => {
    writeConsent({ analytics: false, marketing: false });
    close();
  }, [close]);

  const saveChoice = useCallback(() => {
    writeConsent(choice);
    close();
  }, [choice, close]);

  // Reopened from the permanent footer link, with the current choices loaded.
  useEffect(() => {
    const handler = () => {
      const current = readConsent();
      setChoice({
        analytics: current?.analytics ?? false,
        marketing: current?.marketing ?? false,
      });
      setOpen(true);
    };
    window.addEventListener('consent:open', handler);
    return () => window.removeEventListener('consent:open', handler);
  }, []);

  // ESC closes as reject-all, never as accept. Dismissing a consent dialog is
  // not consent, and treating it as such is the single most common dark
  // pattern in this category.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        rejectAll();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, input, a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    firstFocusable.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, rejectAll]);

  if (!CONSENT_REQUIRED) {
    // With the layer off there is nothing to consent to, but the footer link
    // is permanent, so it must still lead somewhere honest.
    return <ConsentUnavailableNotice />;
  }
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id="consent-title" className="text-lg font-semibold text-slate-900">
          Nastavení cookies
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          Souhlas můžete kdykoli odvolat odkazem „Nastavení cookies“ v patičce. Odmítnutí nijak
          neomezí používání mapy.
        </p>

        <div className="mt-4 space-y-3">
          {/* Necessary: always on, explained, and deliberately not a toggle —
              presenting a switch that cannot be switched is misleading. */}
          <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-medium text-slate-900">Nezbytné</h3>
              <span className="text-sm text-slate-600">vždy aktivní</span>
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Anonymní identifikátor hlasování, přihlášení administrátora a lokální cache obsahu.
              Bez nich by web nefungoval; nelze je vypnout a neslouží k sledování.
            </p>
          </section>

          <ConsentToggle
            id="analytics"
            label="Analytické"
            description="Měření návštěvnosti. Aktuálně na webu žádné nepoužíváme."
            checked={choice.analytics}
            onChange={(v) => setChoice((c) => ({ ...c, analytics: v }))}
          />
          <ConsentToggle
            id="marketing"
            label="Marketingové"
            description="Reklamní a remarketingové nástroje. Aktuálně na webu žádné nepoužíváme."
            checked={choice.marketing}
            onChange={(v) => setChoice((c) => ({ ...c, marketing: v }))}
          />
        </div>

        {/*
          Reject and accept sit at the same level, in the same size, with the
          same visual weight. No greyed-out reject, no reject hidden a layer
          down — that is the requirement, not a stylistic preference.
        */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            ref={firstFocusable}
            type="button"
            onClick={rejectAll}
            className="flex-1 rounded-md border border-brand-700 bg-white px-4 py-2.5 font-semibold text-brand-700 hover:bg-brand-50"
          >
            Odmítnout vše
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="flex-1 rounded-md border border-brand-700 bg-brand-700 px-4 py-2.5 font-semibold text-white hover:bg-brand-600"
          >
            Přijmout vše
          </button>
          <button
            type="button"
            onClick={saveChoice}
            className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2.5 font-medium text-slate-700 hover:bg-slate-100"
          >
            Uložit výběr
          </button>
        </div>

        <p className="mt-3 text-xs text-slate-600">
          <Link className="underline" to="/soukromi">
            Jaké údaje zpracováváme a proč
          </Link>
        </p>
      </div>
    </div>
  );
}

function ConsentToggle({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <section className="rounded-md border border-slate-200 p-3">
      <div className="flex items-start gap-3">
        <input
          id={`consent-${id}`}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-5 w-5 accent-brand-700"
        />
        <div>
          <label htmlFor={`consent-${id}`} className="font-medium text-slate-900">
            {label}
          </label>
          <p className="text-sm text-slate-700">{description}</p>
        </div>
      </div>
    </section>
  );
}

/** Shown when the footer link is used while the consent layer is off. */
function ConsentUnavailableNotice() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('consent:open', handler);
    return () => window.removeEventListener('consent:open', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-none-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id="consent-none-title" className="text-lg font-semibold text-slate-900">
          Nastavení cookies
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          Není co nastavovat. Tento web nepoužívá žádné analytické ani marketingové cookies, žádné
          externí měřicí skripty, žádná vložená videa ani písma z cizích serverů. Ukládá se pouze
          to, co je nezbytné pro funkci webu — přehled najdete v{' '}
          <Link className="underline" to="/soukromi">
            zásadách ochrany soukromí
          </Link>
          .
        </p>
        <button
          type="button"
          autoFocus
          onClick={() => setOpen(false)}
          className="mt-4 rounded-md border border-brand-700 bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-600"
        >
          Zavřít
        </button>
      </div>
    </div>
  );
}
